"use client";

import { c as _c } from "react/compiler-runtime";
import { Mic, MicOff, PhoneOff, ScreenShare, ScreenShareOff, UserPlus } from "lucide-react";
import { cn } from "@/lib/utils";
import { callEngine } from "../model/call-engine";
interface CallControlsProps {
  readonly isMuted: boolean;
  readonly isSharing: boolean;
  /** Chime allows one content share at a time — block starting a second. */
  readonly shareBlocked: boolean;
  /** Invite only makes sense on a connected call (the room must be live). */
  readonly canInvite: boolean;
  readonly inviteOpen: boolean;
  readonly onToggleInvite: () => void;
  readonly onToggleMute: () => void;
  readonly onHangup: () => void;
}

/** The mute / share / hang-up dock for a direct call — shared by both stage
 *  views. Mirrors MeetingControls; media actions go straight to the engine. */
export function CallControls(t0) {
  const $ = _c(37);
  const {
    isMuted,
    isSharing,
    shareBlocked,
    canInvite,
    inviteOpen,
    onToggleInvite,
    onToggleMute,
    onHangup
  } = t0;
  const t1 = isMuted ? "Unmute" : "Mute";
  const t2 = isMuted ? "Unmute" : "Mute";
  const t3 = isMuted ? "bg-red-500/90 text-white hover:bg-red-500" : "bg-white/15 text-white hover:bg-white/20";
  let t4;
  if ($[0] !== t3) {
    t4 = cn("flex h-10 w-10 items-center justify-center rounded-full transition-all active:scale-[0.97] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80", t3);
    $[0] = t3;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== isMuted) {
    t5 = isMuted ? <MicOff className="h-5 w-5" /> : <Mic className="h-5 w-5" />;
    $[2] = isMuted;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== isMuted || $[5] !== onToggleMute || $[6] !== t1 || $[7] !== t2 || $[8] !== t4 || $[9] !== t5) {
    t6 = <button type="button" onClick={onToggleMute} aria-pressed={isMuted} aria-label={t1} title={t2} data-testid="call-mute" className={t4}>{t5}</button>;
    $[4] = isMuted;
    $[5] = onToggleMute;
    $[6] = t1;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== isSharing) {
    t7 = () => isSharing ? callEngine.stopScreenShare() : void callEngine.startScreenShare();
    $[11] = isSharing;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  const t8 = isSharing ? "Stop sharing your screen" : "Share your screen";
  const t9 = shareBlocked ? "Someone else is sharing" : isSharing ? "Stop sharing" : "Share your screen";
  const t10 = isSharing ? "bg-[#5651ff] text-white hover:bg-[#4a45e0]" : "bg-white/15 text-white hover:bg-white/20";
  let t11;
  if ($[13] !== t10) {
    t11 = cn("flex h-10 w-10 items-center justify-center rounded-full transition-all active:scale-[0.97] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80 disabled:opacity-40", t10);
    $[13] = t10;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== isSharing) {
    t12 = isSharing ? <ScreenShareOff className="h-5 w-5" /> : <ScreenShare className="h-5 w-5" />;
    $[15] = isSharing;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== isSharing || $[18] !== shareBlocked || $[19] !== t11 || $[20] !== t12 || $[21] !== t7 || $[22] !== t8 || $[23] !== t9) {
    t13 = <button type="button" onClick={t7} disabled={shareBlocked} aria-pressed={isSharing} aria-label={t8} title={t9} data-testid="call-share" className={t11}>{t12}</button>;
    $[17] = isSharing;
    $[18] = shareBlocked;
    $[19] = t11;
    $[20] = t12;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== canInvite || $[26] !== inviteOpen || $[27] !== onToggleInvite) {
    t14 = canInvite ? <button type="button" onClick={onToggleInvite} aria-pressed={inviteOpen} aria-label="Add someone to the call" title="Add someone to the call" data-testid="call-invite" className={cn("flex h-10 w-10 items-center justify-center rounded-full transition-all active:scale-[0.97] focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80", inviteOpen ? "bg-[#5651ff] text-white hover:bg-[#4a45e0]" : "bg-white/15 text-white hover:bg-white/20")}><UserPlus className="h-5 w-5" /></button> : null;
    $[25] = canInvite;
    $[26] = inviteOpen;
    $[27] = onToggleInvite;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <PhoneOff className="h-4 w-4" />;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  let t16;
  if ($[30] !== onHangup) {
    t16 = <button type="button" onClick={onHangup} aria-label="Hang up" title="Hang up" data-testid="call-leave" className="flex h-10 items-center gap-1.5 rounded-full bg-red-500 px-3.5 text-sm font-semibold text-white transition-all active:scale-[0.97] hover:bg-red-600 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-white/80">{t15}End</button>;
    $[30] = onHangup;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  let t17;
  if ($[32] !== t13 || $[33] !== t14 || $[34] !== t16 || $[35] !== t6) {
    t17 = <div className="flex items-center justify-center gap-2 rounded-full bg-white/6 p-1.5 ring-1 ring-white/10">{t6}{t13}{t14}{t16}</div>;
    $[32] = t13;
    $[33] = t14;
    $[34] = t16;
    $[35] = t6;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  return t17;
}